import { aggressSubmitErrorText } from '../../helpers/orders'
import { addOrUpdateElement } from '../helpers'
import { SetListTradingWatchlistIdAction } from '../listTrading/actions'
import { OrderAction } from './actions'
import {
  addOrUpdateNewOperatorOrders,
  applyFakeTransactionId,
  buildTempAggressorOrder,
  buildTempOrder,
  getUserOrderKey,
  isActiveUserOrder,
  ResubmitOrders,
  updateFakeTransactionIdMap,
  updateHitLiftErrors,
  upsertUserOrders
} from './helpers'
import { Order, OrderType, ValidationResult } from './types'
import { RemoveAllStagedOrdersAction } from '../stagedOrders/actions'

export type ListTradeErrorValue = {
  size: Order['size']
  error: Partial<Error>
}

export interface State {
  myOrdersIsOpen: { [gridIndex: number]: boolean }
  /*
    Canonical collection of the user's own orders in every status, keyed by
    getUserOrderKey. Views by status / security / transaction id are memoized
    selector projections over this collection.
   */
  userOrders: Record<string, Order>
  operatorOrders: Record<string, Order>
  resubmitOrders: ResubmitOrders
  fakeTransactionIdMap: Record<number, number>
  hitLiftErrors: Record<number, string>
  listTradeOrders: Record<number, Order[]>
  listTradeErrors: Record<Order['id'], ListTradeErrorValue>
  validations: Array<ValidationResult & { date: Date }>
  ordersFetched: boolean
  selectedValidation: { securityId: number; orderType: OrderType } | undefined
  validationOpen: boolean
  tradeConfirmId: number | null
}

export const getInitialState = () =>
  ({
    myOrdersIsOpen: {},
    userOrders: {},
    operatorOrders: {},
    resubmitOrders: {},
    fakeTransactionIdMap: {},
    hitLiftErrors: {},
    listTradeErrors: {},
    listTradeOrders: {},
    validations: [],
    ordersFetched: false,
    selectedValidation: undefined,
    validationOpen: false,
    tradeConfirmId: null
  } as State)

const updateValidation = (
  validations: State['validations'],
  validation: ValidationResult
) =>
  addOrUpdateElement(
    validations,
    { ...validation, date: new Date() },
    (firstValidation, secondValidation) =>
      firstValidation.securityId === secondValidation.securityId &&
      firstValidation.orderType === secondValidation.orderType
  ).filter((v) => v.error !== undefined)

const checkValidation = (
  errorsCrossedFind: Order | undefined,
  validations: State['validations']
) => {
  if (errorsCrossedFind) {
    const validationResult: ValidationResult = {
      securityId: errorsCrossedFind.securityId,
      orderType: errorsCrossedFind.type,
      error: errorsCrossedFind.statusReason,
      isin: errorsCrossedFind.isin
    }
    return updateValidation(validations, validationResult)
  } else {
    return validations
  }
}

/* Flip an active order to error status; no-op for unknown or finished orders. */
const setOrderErrorStatus = (state: State, orderId: Order['id']): State => {
  const order: Order | undefined = state.userOrders[orderId]
  if (!order || !isActiveUserOrder(order)) {
    return state
  }
  const { userOrders, resubmitOrders } = upsertUserOrders(
    state.userOrders,
    state.resubmitOrders,
    [{ ...order, status: 'error' }]
  )
  return { ...state, userOrders, resubmitOrders }
}

export default (
  state = getInitialState(),
  action:
    | OrderAction
    | SetListTradingWatchlistIdAction
    | RemoveAllStagedOrdersAction
): State => {
  switch (action.type) {
    case 'order.setMyOrdersOpen':
      return {
        ...state,
        myOrdersIsOpen: {
          ...state.myOrdersIsOpen,
          [action.payload.gridIndex]: action.payload.open
        }
      }
    case 'order.updateValidation':
      return {
        ...state,
        validations: updateValidation(state.validations, action.payload)
      }
    case 'order.updateValidations':
      return {
        ...state,
        validations: action.payload.reduce(updateValidation, state.validations)
      }
    case 'order.setValidation':
      return {
        ...state,
        selectedValidation: {
          securityId: action.payload.securityId,
          orderType: action.payload.orderType
        }
      }
    case 'stagedOrders.removeAllStagedOrders':
      return {
        ...state,
        validations: []
      }
    case 'order.clearMyOrders':
      return {
        ...state,
        userOrders: {}
      }

    case 'order.resetValidations':
      return { ...state, validations: [] }
    case `listTrading.setListTradingWatchlistId`:
      return {
        ...state,
        listTradeErrors: {},
        listTradeOrders: {}
      }
    case 'order.createTempAggressorOrder': {
      const tempOrder = buildTempAggressorOrder(action.payload)
      return {
        ...state,
        userOrders: {
          ...state.userOrders,
          [getUserOrderKey(tempOrder)]: tempOrder
        }
      }
    }
    case 'order.createTempOrder': {
      const tempOrder = buildTempOrder(action.payload)
      return {
        ...state,
        userOrders: {
          ...state.userOrders,
          [getUserOrderKey(tempOrder)]: tempOrder
        }
      }
    }
    case 'order.submit': {
      if (!action.payload.listId) {
        return state
      }
      const ltOrder = action.payload.order
      const securityOrders = (
        state.listTradeOrders[ltOrder.securityId] ?? []
      ).filter((o) => o.id !== action.payload.orderId)
      const { [action.payload.orderId]: deleted, ...ltErrors } =
        state.listTradeErrors
      return {
        ...state,
        listTradeErrors: ltErrors,
        listTradeOrders: { [ltOrder.securityId]: [...securityOrders, ltOrder] }
      }
    }
    case 'order.submitFailure': {
      /*
        Fired only by the HitOrLiftOrder epic; orderId is the AGGRESSED resting
        order, so it's normally not in userOrders and the error routes below to
        hitLiftErrors (AggressorModal/alert cards) or listTradeErrors. Only a
        failure against our own still-active order becomes order error status
        (SOE surface); our own terminal orders fall through on purpose — marking
        a dead order errored surfaces nowhere. SOE submit failures never come
        here (they arrive via createOrderError / checkValidation).
      */
      const failedOrder: Order | undefined =
        state.userOrders[action.payload.orderId]
      if (failedOrder && isActiveUserOrder(failedOrder)) {
        return setOrderErrorStatus(state, action.payload.orderId)
      }
      if (action.payload.listId) {
        return {
          ...state,
          listTradeErrors: {
            ...state.listTradeErrors,
            [action.payload.orderId]: {
              error: action.payload.error,
              size: action.payload.size
            }
          }
        }
      }
      return {
        ...state,
        hitLiftErrors: updateHitLiftErrors(
          state.hitLiftErrors,
          action.payload.transactionId,
          action.payload.error.message ||
            action.payload.error.errorCode ||
            aggressSubmitErrorText
        )
      }
    }
    case 'order.setOrderError':
      return setOrderErrorStatus(state, action.payload)
    case 'order.cancel': {
      const orderToCancel: Order | undefined =
        state.userOrders[action.payload.orderId]
      if (!orderToCancel) {
        return state
      }
      return {
        ...state,
        validations: state.validations.filter(
          (val) => val.securityId !== orderToCancel.securityId
        )
      }
    }
    case 'order.cancelFailure':
      return setOrderErrorStatus(state, action.payload.orderId)
    case 'order.addOrUpdateUserOrders': {
      const { userOrders, resubmitOrders } = upsertUserOrders(
        state.userOrders,
        state.resubmitOrders,
        action.payload
      )
      const errorsCrossed = action.payload.find(
        ({ status }) => status === 'error'
      )
      const validations = checkValidation(errorsCrossed, state.validations)
      // stream echo changed nothing: keep the slice identity so subscribers skip
      if (
        userOrders === state.userOrders &&
        resubmitOrders === state.resubmitOrders &&
        validations === state.validations &&
        state.ordersFetched
      ) {
        return state
      }
      return {
        ...state,
        userOrders,
        resubmitOrders,
        ordersFetched: true,
        validations
      }
    }
    case 'order.addOrUpdateOperatorOrders': {
      const operatorOrders = addOrUpdateNewOperatorOrders(
        state.operatorOrders,
        action.payload
      )
      // stream echo changed nothing: keep the slice identity so subscribers skip
      if (operatorOrders === state.operatorOrders) {
        return state
      }
      return { ...state, operatorOrders }
    }

    case 'order.mapFakeTransactionId':
      return {
        ...state,
        userOrders: applyFakeTransactionId(
          state.userOrders,
          action.payload.fakeTransactionId,
          action.payload.transactionId
        ),
        fakeTransactionIdMap: updateFakeTransactionIdMap(
          state.fakeTransactionIdMap,
          action.payload.fakeTransactionId,
          action.payload.transactionId
        )
      }
    case 'order.createOrderError': // error from the backend
      return {
        ...state,
        validations: [
          ...state.validations,
          {
            securityId: action.payload.securityId,
            orderType: action.payload.orderType,
            error: action.payload.err.message,
            isin: action.payload.isin,
            date: new Date()
          }
        ]
      }
    case 'order.createOrderBulkError': // error from the backend
      return {
        ...state,
        validations: state.validations.concat(
          action.payload.error.map((a) => {
            return {
              securityId: a.error.payload.securityId,
              orderType: a.error.payload.orderType,
              error: a.error.payload.err.message,
              isin: a.error.payload.isin,
              date: a.date
            }
          })
        )
      }
    case 'order.setValidationOpen':
      return {
        ...state,
        validationOpen: action.payload.validationOpen
      }
    case 'order.loadResubmitOrders':
      return {
        ...state,
        resubmitOrders: action.payload
      }
    case 'order.setTradeConfirmId':
      return {
        ...state,
        tradeConfirmId: action.payload.tradeConfirmId
      }
    default:
      return state
  }
}
